import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Crown, Star, CheckCircle, Sparkles, Check, X, Phone, Mail, Clock } from 'lucide-react'
import { usePublicSubscriptionTiers, config, useClientConfig } from '@workspace/public-utils'
import { Seo } from '../components/Seo'

interface CompanyInfo {
  name: string | null
  contact_email: string | null
  contact_number: string | null
  contact_number_secondary: string | null
  working_hours: string | null
  address: string | null
  bank_info: any | null
}

// Tier icons and colors matching HANA ProbX branding
const tierConfig: Record<string, {
  icon: any;
  gradient: string;
  textColor: string;
  iconColor: string;
  accentColor: string;
}> = {
  SELECT: {
    icon: CheckCircle,
    gradient: 'from-[#F5EBE0] to-[#E5D3C8]',
    textColor: 'text-gray-900',
    iconColor: 'text-amber-600',
    accentColor: 'bg-amber-600'
  },
  STANDARD: {
    icon: CheckCircle,
    gradient: 'from-[#C84B31] to-[#A73A24]',
    textColor: 'text-white',
    iconColor: 'text-amber-400',
    accentColor: 'bg-amber-400'
  },
  PRIME: {
    icon: Sparkles,
    gradient: 'from-[#D4AF37] to-[#B8941F]',
    textColor: 'text-gray-900',
    iconColor: 'text-amber-700',
    accentColor: 'bg-amber-700'
  },
  PRESTIGE: {
    icon: Star,
    gradient: 'from-[#E8B4BC] to-[#D9A5AD]',
    textColor: 'text-gray-900',
    iconColor: 'text-pink-600',
    accentColor: 'bg-pink-600'
  },
  ROYAL: {
    icon: Crown,
    gradient: 'from-[#1E3A5F] to-[#0F1E2E]',
    textColor: 'text-white',
    iconColor: 'text-amber-400',
    accentColor: 'bg-amber-400'
  }
}

interface PlanCardProps {
  tier: any;
  index: number;
  isPopular?: boolean;
  onConsultClick: () => void;
}

const HorizontalPlanCard: React.FC<PlanCardProps> = ({ tier, index, isPopular, onConsultClick }) => {
  const config = tierConfig[tier.name as keyof typeof tierConfig] || tierConfig.SELECT
  const Icon = config.icon
  
  // Handle sold out and disabled states
  const isSoldOut = !!tier.sold_out_mark
  const isDisabled = tier.is_disabled || false
  const soldOutColor = tier.sold_out_color || 'RED'
  
  // Color mapping for sold out badge
  const badgeColors: Record<string, string> = {
    RED: 'bg-red-600 text-white',
    ORANGE: 'bg-orange-600 text-white',
    YELLOW: 'bg-yellow-500 text-gray-900',
    BLUE: 'bg-blue-600 text-white',
    SILVER: '', // Black background will be applied via silver-soldout-badge class
  }

  return (
    <motion.div
      className="relative w-full"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      whileHover={!isDisabled ? { y: -4 } : {}}
    >
      {/* Sold Out Badge */}
      {isSoldOut && (
        <div className="absolute -top-3 -right-3 z-20">
          <div className={`${badgeColors[soldOutColor]} ${soldOutColor === 'SILVER' ? 'silver-soldout-badge' : ''} px-6 py-2 rounded-full text-2xl font-bold shadow-lg transform rotate-12`}>
            {soldOutColor === 'SILVER' ? (
              <span className="shimmer-silver-text">{tier.sold_out_mark}</span>
            ) : (
              tier.sold_out_mark
            )}
          </div>
        </div>
      )}

      {/* Popular Badge */}
      {isPopular && !isSoldOut && (
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
          <span className="bg-gradient-to-r from-happiness-primary to-happiness-secondary text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg">
            인기 플랜
          </span>
        </div>
      )}
      
      {/* Horizontal Card */}
      <div 
        className={`
          bg-gradient-to-r ${config.gradient} ${config.textColor} 
          rounded-2xl shadow-xl overflow-hidden 
          ${isDisabled ? 'opacity-50 grayscale cursor-not-allowed' : 'hover:shadow-2xl'}
          transition-all duration-300
        `}
      >
        <div className="flex flex-col md:flex-row items-center p-6 md:p-8 gap-6">
          {/* Left Section - Icon & Name */}
          <div className="flex items-center gap-6 md:w-1/4">
            <div className={`w-16 h-16 rounded-full border-4 ${config.textColor === 'text-white' ? 'border-white/30' : 'border-gray-900/20'} flex items-center justify-center flex-shrink-0`}>
              <Icon className={`w-8 h-8 ${config.iconColor}`} strokeWidth={2.5} />
            </div>
            <div>
              <h3 className="text-3xl font-black tracking-tight !text-white">{tier.name}</h3>
              <p className={`text-base font-bold ${config.textColor === 'text-white' ? 'text-white opacity-90' : 'text-white opacity-80'}`}>
                {tier.top_tier_comment || 
                  (tier.name === 'SELECT' ? 'Basic' :
                   tier.name === 'STANDARD' ? 'Selected' :
                   tier.name === 'PRIME' ? 'Premium' :
                   tier.name === 'PRESTIGE' ? 'High-level' :
                   'Top-tier')}
              </p>
            </div>
          </div>

          {/* Middle Section - Features */}
          <div className="flex-1 md:w-2/5">
            <div className={`${config.textColor === 'text-white' ? 'bg-white/10' : 'bg-black/5'} backdrop-blur-sm rounded-xl p-4`}>
              <h4 className="text-sm font-bold mb-2 opacity-70">제공 기능</h4>
              {tier.features && tier.features.length > 0 ? (
                <ul className="space-y-1">
                  {tier.features.slice(0, 3).map((feature: string, idx: number) => (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      <Check className={`w-4 h-4 flex-shrink-0 mt-0.5 ${config.iconColor}`} />
                      <span className="line-clamp-1">{feature}</span>
                    </li>
                  ))}
                  {tier.features.length > 3 && (
                    <li className="text-xs opacity-70">외 {tier.features.length - 3}개 기능</li>
                  )}
                </ul>
              ) : (
                <p className="text-sm opacity-70">기본 기능 제공</p>
              )}
            </div>
          </div>

          {/* Right Section - CTA */}
          <div className="flex flex-col items-center gap-4 md:w-1/3">
            <div className="text-center">
              <div className={`text-lg ${config.textColor === 'text-white' ? 'opacity-80' : 'opacity-70'}`}>
                {tier.max_numbers_per_month}회 생성 가능
              </div>
            </div>
            
            {!isDisabled ? (
              <button
                onClick={onConsultClick}
                className={`
                  block w-full py-3 px-6 rounded-xl font-bold text-center transition-all duration-300 transform hover:scale-105
                  ${isPopular 
                    ? 'bg-white text-happiness-primary shadow-lg hover:shadow-xl' 
                    : config.textColor === 'text-white'
                    ? 'bg-white/20 hover:bg-white/30 backdrop-blur-sm'
                    : 'bg-gray-900/10 hover:bg-gray-900/20'
                  }
                `}
              >
                상담신청
              </button>
            ) : (
              <button
                disabled
                className={`
                  block w-full py-3 px-6 rounded-xl font-bold text-center cursor-not-allowed
                  ${config.textColor === 'text-white' ? 'bg-white/10' : 'bg-gray-900/10'}
                `}
              >
                {isSoldOut ? tier.sold_out_mark : '이용 불가'}
              </button>
            )}
          </div>
        </div>

        {/* Description (if exists) */}
        {tier.description && (
          <div className={`px-6 pb-6 ${config.textColor === 'text-white' ? 'opacity-80' : 'opacity-70'}`}>
            <p className="text-sm text-center">{tier.description}</p>
          </div>
        )}
      </div>
    </motion.div>
  )
}

// Free Registration Form Component
const FreeRegistrationForm: React.FC = () => {
  const [name, setName] = useState('')
  const [mobileNumber, setMobileNumber] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')

  // Phone number normalization function
  const normalizePhoneNumber = (value: string): string => {
    // Remove all non-digit characters
    const digitsOnly = value.replace(/\D/g, '')
    
    // Format based on length
    if (digitsOnly.length <= 3) {
      return digitsOnly
    } else if (digitsOnly.length <= 7) {
      return `${digitsOnly.slice(0, 3)}-${digitsOnly.slice(3)}`
    } else if (digitsOnly.length <= 11) {
      return `${digitsOnly.slice(0, 3)}-${digitsOnly.slice(3, 7)}-${digitsOnly.slice(7, 11)}`
    }
    // Limit to 11 digits
    return `${digitsOnly.slice(0, 3)}-${digitsOnly.slice(3, 7)}-${digitsOnly.slice(7, 11)}`
  }

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = normalizePhoneNumber(e.target.value)
    setMobileNumber(formatted)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsSubmitting(true)

    try {
      const response = await fetch(`${config.API_BASE_URL}/api/v1/public/auth/free-register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: name.trim(),
          mobile_number: mobileNumber.trim()
        })
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.detail || '회원가입에 실패했습니다')
      }

      // Show success popup
      alert('신청되었습니다!')
      
      // Reset form
      setName('')
      setMobileNumber('')
    } catch (err: any) {
      setError(err.message || '회원가입 중 오류가 발생했습니다')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="text-left">
        <label className="block text-white mb-2 font-semibold">이름</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          placeholder="이름을 입력하세요"
          className="w-full px-4 py-3 rounded-xl border-2 border-white/20 bg-white/10 text-white placeholder-white/60 focus:outline-none focus:border-white transition-colors"
          data-testid="free-registration-name"
        />
      </div>

      <div className="text-left">
        <label className="block text-white mb-2 font-semibold">휴대폰 번호</label>
        <input
          type="tel"
          value={mobileNumber}
          onChange={handlePhoneNumberChange}
          required
          placeholder="010-1234-5678"
          className="w-full px-4 py-3 rounded-xl border-2 border-white/20 bg-white/10 text-white placeholder-white/60 focus:outline-none focus:border-white transition-colors"
          data-testid="free-registration-mobile"
          maxLength={13}
        />
      </div>

      {error && (
        <div className="bg-red-500/20 border-2 border-red-500 text-white px-4 py-3 rounded-xl">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full py-4 px-6 bg-white text-happiness-primary font-bold rounded-xl hover:bg-gray-100 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed text-lg shadow-lg"
        data-testid="free-registration-submit"
      >
        {isSubmitting ? '처리 중...' : '번호 받기 신청하기'}
      </button>
    </form>
  )
}

const ConsultationModal: React.FC<{ isOpen: boolean; onClose: () => void; companyInfo: CompanyInfo | null }> = ({ isOpen, onClose, companyInfo }) => {
  if (!isOpen) return null

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.2 }}
          className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>

          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-happiness-primary to-happiness-secondary rounded-full flex items-center justify-center mx-auto mb-4">
              <Phone className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl font-black text-gray-900 mb-2">
              플랜 가입 문의
            </h2>
            <p className="text-gray-600">
              아래 연락처로 문의주시면 친절하게 안내해드리겠습니다
            </p>
          </div>

          <div className="space-y-4">
            {companyInfo?.contact_number && (
              <a
                href={`tel:${companyInfo.contact_number.replace(/[^0-9]/g, '')}`}
                className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
              >
                <div className="w-12 h-12 bg-happiness-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-happiness-primary" />
                </div>
                <div className="flex-1">
                  <div className="text-sm text-gray-600">대표 연락처</div>
                  <div className="text-xl font-bold text-gray-900">{companyInfo.contact_number}</div>
                </div>
              </a>
            )}

            {companyInfo?.contact_number_secondary && (
              <a
                href={`tel:${companyInfo.contact_number_secondary.replace(/[^0-9]/g, '')}`}
                className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
              >
                <div className="w-12 h-12 bg-happiness-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-happiness-primary" />
                </div>
                <div className="flex-1">
                  <div className="text-sm text-gray-600">고객센터 연락처</div>
                  <div className="text-xl font-bold text-gray-900">{companyInfo.contact_number_secondary}</div>
                </div>
              </a>
            )}

            {companyInfo?.contact_email && (
              <a
                href={`mailto:${encodeURIComponent(companyInfo.contact_email.trim())}`}
                className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
              >
                <div className="w-12 h-12 bg-happiness-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-happiness-primary" />
                </div>
                <div className="flex-1">
                  <div className="text-sm text-gray-600">이메일</div>
                  <div className="text-lg font-semibold text-gray-900">{companyInfo.contact_email}</div>
                </div>
              </a>
            )}

            {companyInfo?.working_hours && (
              <div className="flex items-center gap-4 p-4 bg-blue-50 rounded-xl">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6 text-blue-600" />
                </div>
                <div className="flex-1">
                  <div className="text-sm text-blue-600">상담 가능 시간</div>
                  <div className="text-lg font-semibold text-gray-900">{companyInfo.working_hours}</div>
                </div>
              </div>
            )}
          </div>

          <button
            onClick={onClose}
            className="w-full mt-6 py-3 px-6 bg-gradient-to-r from-happiness-primary to-happiness-secondary text-white font-bold rounded-xl hover:shadow-lg transition-all duration-300"
          >
            닫기
          </button>
        </motion.div>
      </div>
    </AnimatePresence>
  )
}

const SubscriptionPlansPageNew: React.FC = () => {
  const clientConfig = useClientConfig()
  const { data: subscriptionTiers, isLoading, error } = usePublicSubscriptionTiers()
  const [showConsultModal, setShowConsultModal] = useState(false)
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null)

  useEffect(() => {
    fetchCompanyInfo()
  }, [])

  const fetchCompanyInfo = async () => {
    try {
      const response = await fetch(`${config.API_BASE_URL}/api/v1/public/company-info`)
      if (response.ok) {
        const data = await response.json()
        setCompanyInfo(data)
      }
    } catch (error) {
      console.error('Failed to fetch company info:', error)
    }
  }

  const handleConsultClick = () => {
    setShowConsultModal(true)
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container-responsive">
          <div className="text-center">
            <div className="spinner mx-auto mb-4"></div>
            <p className="text-gray-600">구독 플랜을 불러오는 중...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container-responsive">
          <div className="text-center">
            <p className="text-red-600 mb-4">구독 플랜을 불러올 수 없습니다.</p>
            <button 
              onClick={() => window.location.reload()}
              className="btn-primary"
            >
              다시 시도
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Seo
        title="구독 플랜"
        description="구독 등급별 혜택과 가격을 확인하고, 나에게 맞는 로또 번호 서비스 플랜을 선택해 보세요."
        canonicalPath="/plans"
      />
      <ConsultationModal isOpen={showConsultModal} onClose={() => setShowConsultModal(false)} companyInfo={companyInfo} />
      
          {/* Hero Section - PRO-BX Branding */}
      <section className="relative bg-white py-12 flex items-center justify-center">
        <div className="relative w-full max-w-2xl mx-auto p-6 text-center">
          {/* Main Service Name */}
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-7xl md:text-8xl font-black tracking-wider text-slate-800 uppercase mb-2"
          >
                PRO<span className="text-pink-500">-BX</span>
          </motion.h1>
          
          {/* Subtitle */}
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="text-lg md:text-xl text-slate-600 tracking-widest font-light mb-6"
          >
            AI ALGORITHM SYSTEM
          </motion.p>

          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="my-6 h-px bg-gradient-to-r from-slate-200 via-pink-500 to-slate-200"
          />

          {/* Korean Slogan */}
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-xl md:text-2xl font-bold text-slate-900 leading-relaxed mb-6"
            style={{ fontFamily: "'Noto Sans KR', sans-serif" }}
          >
            "당첨되지 않은 이유를 분석할 때,<br />비로소 확률은 움직인다"
          </motion.p>
          
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="my-6 h-px bg-gradient-to-r from-slate-200 via-blue-400 to-slate-200"
          />

          {/* Footer Information */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="text-slate-500"
          >
            <p className="text-sm">Powered by</p>
            <p className="text-2xl font-bold mt-1" style={{ fontFamily: "'Noto Sans KR', sans-serif" }}>
              <span className="inline-block align-middle">{clientConfig.brandNameEn} {clientConfig.brandName}</span>
            </p>
            <p className="mt-3 text-xs tracking-widest text-slate-400">
              PROB-X AI DIVISION
            </p>
          </motion.div>
        </div>
      </section>

      {/* Plans Section - Horizontal Cards */}
      <section className="py-20 bg-gradient-to-b from-white via-gray-50 to-white">
        <div className="container-responsive max-w-6xl mx-auto px-4">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
              플랜별 상세 비교
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              당신에게 맞는 완벽한 플랜을 선택하세요
            </p>
          </motion.div>

          {subscriptionTiers && subscriptionTiers.length > 0 ? (
            <div className="space-y-6">
              {subscriptionTiers
                .sort((a: any, b: any) => a.price - b.price)
                .map((tier: any, index: number) => (
                  <HorizontalPlanCard
                    key={tier.id}
                    tier={tier}
                    index={index}
                    isPopular={false}
                    onConsultClick={handleConsultClick}
                  />
                ))}
            </div>
          ) : (
            <div className="text-center text-gray-600 py-12">
              <p className="text-xl">구독 플랜을 불러올 수 없습니다.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section - Free Registration Form */}
      <section className="py-20 hero-korean">
        <div className="container-responsive text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-md mx-auto"
          >
            <h2 className="text-4xl md:text-5xl font-black mb-6 hero-text-shadow">
              번호 받기 신청
            </h2>
            <p className="text-xl md:text-2xl mb-8 opacity-90">
              이름과 휴대폰 번호만 입력하시면<br />
              무료로 로또 번호를 받아보실 수 있습니다
            </p>
            <FreeRegistrationForm />
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default SubscriptionPlansPageNew

